import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
	Box,
	CircularProgress,
	Link as MuiLink,
	Typography,
} from '@mui/material';
import AcceptanceRevocationPanel from '../Components/AcceptanceRevocationPanel';
import CustomerConfirmationLayout from '../Components/CustomerConfirmationLayout';
import { formatOrgContactLine } from '../Shared/customerContact';
import { fetchBidResponseContext } from '../Shared/publicApi';

const statusConfig = {
	accepted: {
		headline: 'Proposal accepted',
		iconColor: 'success.main',
		buildBody: ({ orgName, requiresDeposit }) => {
			const org = orgName || 'the contractor';
			const base = `Thank you. ${org} has been notified and will follow up with next steps.`;
			if (requiresDeposit) {
				return `${base} Check your email for deposit payment instructions.`;
			}
			return base;
		},
	},
	revoked: {
		headline: 'Acceptance cancelled',
		iconColor: 'info.main',
		buildBody: ({ orgName }) => {
			const org = orgName || 'the contractor';
			return `Your acceptance has been cancelled and the proposal is open again. Contact ${org} if you have questions.`;
		},
	},
	already_revoked: {
		headline: 'Already cancelled',
		iconColor: 'info.main',
		buildBody: () => 'This acceptance was already cancelled.',
	},
	cannot_revoke: {
		headline: 'Cannot cancel acceptance',
		iconColor: 'warning.main',
		buildBody: ({ orgName }) => {
			const org = orgName || 'the contractor';
			return `The cancellation window has passed or this proposal can no longer be changed online. Contact ${org} if you need help.`;
		},
	},
	declined: {
		headline: 'Response recorded',
		iconColor: 'text.secondary',
		buildBody: ({ orgName }) => {
			const org = orgName || 'us';
			return `Thank you for letting us know. Contact ${org} if anything changes.`;
		},
	},
	already_confirmed: {
		headline: 'Already accepted',
		iconColor: 'info.main',
		buildBody: () => 'This proposal was already accepted.',
	},
	already_declined: {
		headline: 'Already declined',
		iconColor: 'info.main',
		buildBody: () => 'This proposal was already declined.',
	},
	cannot_confirm: {
		headline: 'Cannot accept',
		iconColor: 'warning.main',
		buildBody: () =>
			'This proposal can no longer be accepted (it was already declined).',
	},
	cannot_decline: {
		headline: 'Cannot decline',
		iconColor: 'warning.main',
		buildBody: () =>
			'This proposal can no longer be declined (it was already accepted).',
	},
	expired: {
		headline: 'Offer expired',
		iconColor: 'warning.main',
		buildBody: ({ orgName }) => {
			const org = orgName || 'us';
			return `This offer has expired. Please contact ${org} if you would still like to discuss the job.`;
		},
	},
	invalid: {
		headline: 'Invalid link',
		iconColor: 'error.main',
		buildBody: () => 'This link is not valid.',
	},
	error: {
		headline: 'Something went wrong',
		iconColor: 'error.main',
		buildBody: ({ orgName }) => {
			const org = orgName || 'us';
			return `Please try again or contact ${org} by phone or email.`;
		},
	},
};

const defaultStatusConfig = {
	headline: 'Thank you',
	iconColor: 'success.main',
	buildBody: () => 'Thank you for your response.',
};

const BidAccepted = () => {
	const navigate = useNavigate();
	const [params] = useSearchParams();
	const status = (params.get('status') || '').toLowerCase();
	const bidId = (params.get('bid_id') || '').trim();
	const token = (params.get('token') || '').trim();
	const [context, setContext] = useState(null);
	const [loading, setLoading] = useState(Boolean(bidId));
	const [loadError, setLoadError] = useState('');

	const refreshContext = useCallback(async () => {
		if (!bidId) {
			setLoading(false);
			return;
		}
		setLoading(true);
		setLoadError('');
		try {
			const result = await fetchBidResponseContext(bidId, token);
			setContext(result);
		} catch {
			setLoadError('This proposal could not be found.');
			setContext(null);
		} finally {
			setLoading(false);
		}
	}, [bidId, token]);

	useEffect(() => {
		refreshContext();
	}, [refreshContext]);

	const config = statusConfig[status] ?? defaultStatusConfig;

	const bodyCopy = useMemo(
		() =>
			config.buildBody({
				orgName: context?.org_name,
				requiresDeposit: context?.requires_deposit,
			}),
		[config, context?.org_name, context?.requires_deposit],
	);

	const contactLine = useMemo(
		() =>
			context
				? formatOrgContactLine({
						orgName: context.org_name,
						orgPhone: context.org_phone,
						orgEmail: context.org_email,
					})
				: null,
		[context],
	);

	const statusPageHref = useMemo(() => {
		if (!bidId) {
			return null;
		}
		const query = new URLSearchParams({ bid_id: bidId });
		if (token) {
			query.set('token', token);
		}
		return `/bid-status?${query.toString()}`;
	}, [bidId, token]);

	const showAcceptancePanel =
		Boolean(context) &&
		(status === 'accepted' ||
			status === 'already_confirmed' ||
			status === 'revoked' ||
			status === 'already_revoked' ||
			Boolean(context.acceptance_revoked_at));

	const handleRevoked = useCallback(() => {
		if (status === 'accepted' || status === 'already_confirmed') {
			const query = new URLSearchParams({
				status: 'revoked',
				bid_id: bidId,
			});
			if (token) {
				query.set('token', token);
			}
			navigate(`/bid-accepted?${query.toString()}`, { replace: true });
			return;
		}
		refreshContext();
	}, [bidId, navigate, refreshContext, status, token]);

	const isPositive = status === 'accepted';

	if (!bidId && status !== 'invalid') {
		return (
			<CustomerConfirmationLayout
				headline="Invalid link"
				icon={
					<InfoOutlinedIcon
						sx={{ fontSize: 56, color: 'error.main' }}
						aria-hidden
					/>
				}
			>
				<Typography color="text.secondary">
					This link is missing a proposal reference.
				</Typography>
			</CustomerConfirmationLayout>
		);
	}

	if (loading && bidId) {
		return (
			<CustomerConfirmationLayout headline="Loading…">
				<Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
					<CircularProgress size={32} />
				</Box>
			</CustomerConfirmationLayout>
		);
	}

	if (loadError && bidId) {
		return (
			<CustomerConfirmationLayout
				headline="Proposal not found"
				icon={
					<InfoOutlinedIcon
						sx={{ fontSize: 56, color: 'error.main' }}
						aria-hidden
					/>
				}
			>
				<Typography color="text.secondary">{loadError}</Typography>
			</CustomerConfirmationLayout>
		);
	}

	return (
		<CustomerConfirmationLayout
			orgLogoUrl={context?.org_logo_url}
			orgName={context?.org_name}
			headline={config.headline}
			icon={
				isPositive ? (
					<CheckCircleOutlineIcon
						sx={{ fontSize: 56, color: config.iconColor }}
						aria-hidden
					/>
				) : (
					<InfoOutlinedIcon
						sx={{ fontSize: 56, color: config.iconColor }}
						aria-hidden
					/>
				)
			}
		>
			{context?.job_title && (
				<Typography variant="subtitle1" color="text.secondary" sx={{ mb: 1 }}>
					{context.job_title}
				</Typography>
			)}
			{context?.amount && showAcceptancePanel && (
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					Amount: ${context.amount}
				</Typography>
			)}
			<Typography
				color="text.secondary"
				sx={{
					mb: showAcceptancePanel || contactLine || statusPageHref ? 2 : 0,
				}}
			>
				{bodyCopy}
			</Typography>

			{showAcceptancePanel && (
				<AcceptanceRevocationPanel
					variant="accepted"
					orgName={context?.org_name}
					acceptedAt={context?.accepted_at}
					acceptanceRevokedAt={context?.acceptance_revoked_at}
					canRevoke={context?.can_revoke}
					acceptanceFinalized={context?.acceptance_finalized}
					revocationDeadline={context?.revocation_deadline}
					token={token}
					bidId={bidId}
					onRevoked={handleRevoked}
				/>
			)}

			{statusPageHref && (
				<Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
					<MuiLink component={Link} to={statusPageHref} underline="hover">
						View current proposal status
					</MuiLink>
				</Typography>
			)}

			{contactLine && (
				<Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
					{contactLine}
				</Typography>
			)}
		</CustomerConfirmationLayout>
	);
};

export default BidAccepted;
